// /en/estate/documents — Wave 73: EN hub of estate & will document families
import { createFileRoute, Link } from "@tanstack/react-router";
import ServiceAuthorityBlock from "@/components/ServiceAuthorityBlock";
import { ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const PATH = "/en/estate/documents";
const URL = `${SITE}${PATH}`;
const TH_URL = `${SITE}/estate/documents`;
const TITLE = "Thai Estate & Will Documents — 6 Cases | NYC";
const DESC =
  "Thai succession paperwork in six cases: wills under the Civil and Commercial Code, estate administrator petitions, foreign wills and court orders, heir documents and death certificates, powers of attorney signed abroad and foreign heirs inheriting Thai property.";

export const Route = createFileRoute("/en/estate/documents/")({
  loader: async () => {
    const { ESTATE_DOC_GROUPS, ESTATE_APOSTILLE_EN, ESTATE_AUTHORITY_EN } = await import("@/data/estate-doc-groups");
    return {
      __d: { ESTATE_DOC_GROUPS, ESTATE_APOSTILLE_EN, ESTATE_AUTHORITY_EN },
      groups: ESTATE_DOC_GROUPS.map((g) => ({
        slug: g.slug,
        title: g.titleEn,
        faqs: g.faqsEn.map((f) => ({ q: f.q, a: f.a })),
      })),
    };
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: ctaTitle(TITLE, PATH, "en") },
      { name: "description", content: ctaDesc(DESC, PATH, "en") },
      { property: "og:title", content: ctaTitle(TITLE, PATH, "en") },
      { property: "og:description", content: ctaDesc(DESC, PATH, "en") },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:locale", content: "en_US" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [
      { rel: "canonical", href: URL },
      { rel: "alternate", hrefLang: "en", href: URL },
      { rel: "alternate", hrefLang: "th-TH", href: TH_URL },
      { rel: "alternate", hrefLang: "x-default", href: TH_URL },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          inLanguage: "en",
          mainEntity: (loaderData?.groups ?? []).flatMap((g) =>
            g.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          ),
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "ItemList",
          "@id": `${URL}#groups`,
          name: "Thai estate and will document cases",
          numberOfItems: (loaderData?.groups ?? []).length,
          itemListElement: (loaderData?.groups ?? []).map((g, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: g.title,
            url: `${URL}/${g.slug}`,
          })),
        }),
      },
    ],
  }),
  component: EstateDocumentsEnPage,
});

function EstateDocumentsEnPage() {
  const { __d: { ESTATE_DOC_GROUPS, ESTATE_APOSTILLE_EN, ESTATE_AUTHORITY_EN } } = Route.useLoaderData() as { __d: typeof import("@/data/estate-doc-groups") };
  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <nav className="mb-4 text-sm text-muted-foreground">Estate documents / Cases</nav>

      <h1 className="text-3xl font-bold tracking-tight md:text-4xl">Thai Estate & Will Documents — 6 Cases</h1>

      <p className="mt-4 text-base leading-8 text-muted-foreground" data-speakable>
        Succession paperwork follows different routes depending on who issued the document, where the assets sit and
        whether the heirs live in Thailand or abroad. This hub splits the work into six cases, each listing the
        documents to prepare, the order of certification and a pre-filing checklist. Requirements differ between
        courts, Land Offices and banks, so confirm with the receiving authority before you start.
      </p>

      <aside className="mt-6 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">{ESTATE_AUTHORITY_EN}</aside>
      <aside className="mt-3 rounded-lg border border-border bg-muted/40 p-4 text-sm leading-7">{ESTATE_APOSTILLE_EN}</aside>

      <ul className="mt-8 grid gap-2 sm:grid-cols-2">
        {ESTATE_DOC_GROUPS.map((g) => (
          <li key={g.slug}>
            <Link to="/en/estate/documents/$group" params={{ group: g.slug }} className="text-primary hover:underline">
              {g.titleEn}
            </Link>
          </li>
        ))}
      </ul>

      {ESTATE_DOC_GROUPS.map((g) => (
        <section key={g.slug} id={g.slug} className="mt-12 scroll-mt-24">
          <h2 className="text-2xl font-semibold">{g.titleEn}</h2>
          <p className="mt-2 text-muted-foreground">{g.summaryEn}</p>
          <div className="mt-4 space-y-4 leading-8">
            {g.bodyEn.slice(0, 2).map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <Link
            to="/en/estate/documents/$group"
            params={{ group: g.slug }}
            className="mt-3 inline-block text-primary hover:underline"
          >
            Read the full {g.titleEn} guide →
          </Link>
        </section>
      ))}

      <p className="mt-10 rounded-lg border border-border bg-muted/30 p-4 text-sm leading-7">
        Send scans by LINE @nycli, phone +66-83-249-4999 or email nycli@ilc.ltd and our team will confirm the steps,
        anything still missing and a realistic timeline for your case.
      </p>
          <ServiceAuthorityBlock topic="estate" locale="en" />
    </main>
  );
}
